import { CustomerInfo, Purchases } from "@revenuecat/purchases-js";

import type { Subscription } from "../interfaces/user-subscription";

let isInitialized = false;
let currentUserId: string | null = null;

export const initializeRevenueCat = async (userId: string): Promise<void> => {
  // If already initialized for the same user, return early
  if (isInitialized && currentUserId === userId) {
    console.log("RevenueCat already initialized for user:", userId);
    return;
  }

  const apiKey = import.meta.env.VITE_REVENUECAT_WEB_API_KEY;
  if (!apiKey) {
    console.warn("RevenueCat Web API key not found in environment variables");
    return;
  }

  try {
    // If switching users (especially from anonymous to authenticated), force reconfigure
    if (isInitialized && currentUserId !== userId) {
      console.log(
        "🔄 Switching RevenueCat user from",
        currentUserId,
        "to",
        userId,
      );
      // Reset initialization to force fresh configuration
      isInitialized = false;
    }

    await Purchases.configure({
      apiKey,
      appUserId: userId,
    });

    isInitialized = true;
    currentUserId = userId;
    console.log("✅ RevenueCat initialized successfully for user:", userId);
  } catch (error) {
    console.error("❌ Failed to initialize RevenueCat:", error);
    throw error;
  }
};

// New function to initialize RevenueCat for anonymous users (for product browsing)
export const initializeRevenueCatAnonymous = async (): Promise<void> => {
  const anonymousId = `anonymous_${Date.now()}`;

  // If already initialized for an anonymous user, return early
  if (isInitialized && currentUserId?.startsWith("anonymous_")) {
    console.log("RevenueCat already initialized for anonymous browsing");
    return;
  }

  const apiKey = import.meta.env.VITE_REVENUECAT_WEB_API_KEY;
  if (!apiKey) {
    console.warn("RevenueCat Web API key not found in environment variables");
    return;
  }

  try {
    await Purchases.configure({
      apiKey,
      appUserId: anonymousId,
    });

    isInitialized = true;
    currentUserId = anonymousId;
    console.log(
      "RevenueCat initialized successfully for anonymous browsing with ID:",
      anonymousId,
    );
  } catch (error) {
    console.error(
      "Failed to initialize RevenueCat for anonymous browsing:",
      error,
    );
    throw error;
  }
};

export const isRevenueCatInitialized = (): boolean => {
  return isInitialized;
};

export const getCustomerInfo = async (): Promise<CustomerInfo | null> => {
  if (!isInitialized) {
    console.warn(
      "RevenueCat not initialized. Call initializeRevenueCat first.",
    );
    return null;
  }

  try {
    if (typeof Purchases === "undefined") {
      console.warn("RevenueCat SDK not available");
      return null;
    }

    const customerInfo = await Purchases.getSharedInstance().getCustomerInfo();
    console.log("customerInfo", customerInfo);
    return customerInfo;
  } catch (error) {
    console.error("Failed to get customer info:", error);
    return null;
  }
};

// New function to get offerings (works for both authenticated and anonymous users)
export const getOfferings = async () => {
  if (!isInitialized) {
    console.warn(
      "RevenueCat not initialized. Call initializeRevenueCat first.",
    );
    return null;
  }

  try {
    if (typeof Purchases === "undefined") {
      console.warn("RevenueCat SDK not available");
      return null;
    }

    const offerings = await Purchases.getSharedInstance().getOfferings();
    console.log("RevenueCat offerings:", offerings);
    return offerings;
  } catch (error) {
    console.error("Failed to get offerings:", error);
    return null;
  }
};

// New function specifically for anonymous users to get offerings
export const getOfferingsAnonymous = async () => {
  try {
    // Initialize for anonymous users if not already initialized
    if (!isInitialized) {
      await initializeRevenueCatAnonymous();
    }

    if (typeof Purchases === "undefined") {
      console.warn("RevenueCat SDK not available");
      return null;
    }

    const offerings = await Purchases.getSharedInstance().getOfferings();
    console.log("RevenueCat offerings (anonymous):", offerings);
    return offerings;
  } catch (error) {
    console.error("Failed to get offerings for anonymous user:", error);
    return null;
  }
};

// New function to handle purchases with custom parameters
export const purchasePackage = async (purchaseParams: {
  rcPackage: any;
  customerEmail?: string;
  skipSuccessPage?: boolean;
}) => {
  if (!isInitialized) {
    console.warn(
      "RevenueCat not initialized. Call initializeRevenueCat first.",
    );
    return null;
  }

  try {
    if (typeof Purchases === "undefined") {
      console.warn("RevenueCat SDK not available");
      return null;
    }

    console.log("🔍 RevenueCat: purchasePackage called with params:", {
      packageIdentifier: purchaseParams.rcPackage?.identifier,
      customerEmail: purchaseParams.customerEmail,
      skipSuccessPage: purchaseParams.skipSuccessPage,
    });

    // Use the correct purchase method with PurchaseParams
    const purchaseResult =
      await Purchases.getSharedInstance().purchase(purchaseParams);
    console.log("Purchase successful:", purchaseResult);
    return purchaseResult;
  } catch (error) {
    console.error("Purchase failed:", error);
    throw error;
  }
};

// Note: restorePurchases is not available in the Web SDK as it's a mobile-only feature

// Check if user has active RevenueCat entitlements
export const hasRevenueCatEntitlements = async (): Promise<boolean> => {
  const customerInfo = await getCustomerInfo();
  if (!customerInfo) return false;

  // Check for "Samphire membership" entitlement (same as mobile app)
  const samphireMembership =
    customerInfo.entitlements.active["Samphire membership"];

  return !!samphireMembership;
};

export const checkRevenueCatSubscription =
  async (): Promise<Subscription | null> => {
    const customerInfo = await getCustomerInfo();
    if (!customerInfo) return null;

    // Check for "Samphire membership" entitlement (same as mobile app)
    const samphireMembership =
      customerInfo.entitlements.active["Samphire membership"];
    if (!samphireMembership) return null;

    return {
      id: samphireMembership.identifier,
      userId: customerInfo.originalAppUserId,
      product: samphireMembership.productIdentifier,
      provider: "revenuecat",
      expiresAt: samphireMembership.expirationDate?.toISOString() ?? null,
    };
  };

export const hasActiveSubscription = async (): Promise<boolean> => {
  const subscription = await checkRevenueCatSubscription();
  if (!subscription || !subscription.expiresAt) return false;

  // Check if the subscription hasn't expired
  const expirationDate = new Date(subscription.expiresAt);
  const now = new Date();
  return expirationDate > now;
};

export const getDetailedSubscriptionInfo =
  async (): Promise<Subscription | null> => {
    const customerInfo = await getCustomerInfo();
    if (!customerInfo) return null;

    // Check for "Samphire membership" entitlement (same as mobile app)
    const samphireMembership =
      customerInfo.entitlements.active["Samphire membership"];
    if (!samphireMembership) return null;

    const productIdentifier = samphireMembership.productIdentifier;
    const subscriptionDetails =
      customerInfo.subscriptionsByProductIdentifier[productIdentifier];

    // Get pricing information from offerings if available
    let cost: Subscription["cost"] = undefined;
    try {
      const offerings = await getOfferings();
      if (offerings?.current?.availablePackages) {
        const matchingPackage = offerings.current.availablePackages.find(
          (pkg: any) =>
            pkg.identifier === productIdentifier ||
            pkg.identifier === `$sn_${productIdentifier}` ||
            pkg.rcBillingProduct?.identifier === productIdentifier,
        );

        if (matchingPackage) {
          const product =
            matchingPackage.webBillingProduct ||
            matchingPackage.rcBillingProduct;
          const currentPrice = product?.currentPrice;

          if (currentPrice) {
            const packageType = matchingPackage.packageType || "UNKNOWN";
            const packageIdentifier = matchingPackage.identifier || "UNKNOWN";

            // Determine period based on package type and identifier
            let period = "";
            if (packageIdentifier === "$sn_nettle_12_mo") {
              period = "monthly";
            } else if (
              packageType === "$rc_monthly" ||
              packageType.toString() === "MONTHLY"
            ) {
              period = "monthly";
            } else if (
              packageType === "$rc_annual" ||
              packageType.toString() === "ANNUAL"
            ) {
              period = "yearly";
            } else if (
              packageType === "$rc_weekly" ||
              packageType.toString() === "WEEKLY"
            ) {
              period = "weekly";
            }

            cost = {
              amount: currentPrice.amount / 100, // Convert from cents
              currency: currentPrice.currency || "USD",
              formattedPrice:
                currentPrice.formattedPrice ||
                `${currentPrice.currency} ${(currentPrice.amount / 100).toFixed(2)}`,
              period: period,
            };
          }
        }
      }
    } catch (error) {
      console.warn("Could not fetch pricing information:", error);
    }

    return {
      id: samphireMembership.identifier,
      userId: customerInfo.originalAppUserId,
      product: samphireMembership.productIdentifier,
      provider: "revenuecat",
      expiresAt: samphireMembership.expirationDate?.toISOString() ?? null,

      // Purchase information
      purchaseDate:
        samphireMembership.latestPurchaseDate?.toISOString() ?? null,
      originalPurchaseDate:
        samphireMembership.originalPurchaseDate?.toISOString() ?? null,

      // Renewal information
      willRenew: samphireMembership.willRenew,
      renewalDate: samphireMembership.willRenew
        ? (samphireMembership.expirationDate?.toISOString() ?? null)
        : null,

      // Product details
      productIdentifier: samphireMembership.productIdentifier,
      store: samphireMembership.store,

      // Billing information
      billingIssueDetectedAt:
        samphireMembership.billingIssueDetectedAt?.toISOString() ?? null,
      unsubscribeDetectedAt:
        samphireMembership.unsubscribeDetectedAt?.toISOString() ?? null,

      // Sandbox/testing
      isSandbox: samphireMembership.isSandbox,

      // Management
      managementURL: customerInfo.managementURL || undefined,

      // Pricing info
      cost: cost,
    };
  };

// Utility function to check if RevenueCat SDK is available
export const isRevenueCatAvailable = (): boolean => {
  return typeof Purchases !== "undefined";
};

// Debug function to provide detailed offerings information
export const debugRevenueCatOfferings = async () => {
  if (!isInitialized) {
    console.warn(
      "RevenueCat not initialized. Call initializeRevenueCat first.",
    );
    return null;
  }

  try {
    if (typeof Purchases === "undefined") {
      console.warn("RevenueCat SDK not available");
      return null;
    }

    const offerings = await Purchases.getSharedInstance().getOfferings();

    console.group("🔍 RevenueCat Debug Information");

    // Overall offerings info
    console.log("📦 Full offerings object:", offerings);
    console.log("🎯 Current offering:", offerings?.current);
    console.log("📋 All offerings:", offerings?.all);

    if (offerings?.current) {
      const current = offerings.current;
      console.log("🆔 Current offering identifier:", current.identifier);
      console.log(
        "📝 Current offering description:",
        (current as any).description,
      );
      console.log(
        "📦 Available packages count:",
        current.availablePackages?.length || 0,
      );

      if (current.availablePackages && current.availablePackages.length > 0) {
        console.log("📋 Package details:");
        current.availablePackages.forEach((pkg: any, index: number) => {
          console.group(
            `📦 Package ${index + 1}: ${pkg.identifier || "Unknown ID"}`,
          );
          console.log("🆔 Identifier:", pkg.identifier);
          console.log("🏷️ Package Type:", pkg.packageType);
          console.log("🌐 Web Billing Product:", pkg.webBillingProduct);
          console.log("💳 RC Billing Product:", pkg.rcBillingProduct);

          const product = pkg.webBillingProduct || pkg.rcBillingProduct;
          if (product) {
            console.log(
              "💰 Product Title:",
              product.title || product.displayName,
            );
            console.log("📄 Product Description:", product.description);
            console.log("🏪 Product Identifier:", product.identifier);
            console.log("💵 Current Price:", product.currentPrice);

            if (product.currentPrice) {
              console.log("  💲 Amount (cents):", product.currentPrice.amount);
              console.log("  💱 Currency:", product.currentPrice.currency);
              console.log(
                "  🏷️ Formatted Price:",
                product.currentPrice.formattedPrice,
              );
            }
          } else {
            console.warn("⚠️ No product found for this package!");
          }
          console.groupEnd();
        });
      } else {
        console.warn("⚠️ No packages found in current offering!");
      }
    } else {
      console.warn("⚠️ No current offering found!");
    }

    // Check all offerings
    if (offerings?.all && Object.keys(offerings.all).length > 0) {
      console.log("🗂️ All available offerings:");
      Object.entries(offerings.all).forEach(
        ([key, offering]: [string, any]) => {
          console.log(
            `  - ${key}: ${offering.identifier} (${offering.availablePackages?.length || 0} packages)`,
          );
        },
      );
    }

    console.groupEnd();
    return offerings;
  } catch (error) {
    console.error("❌ Failed to debug offerings:", error);
    return null;
  }
};
